import type { auth as authContracts } from '@personal-blog/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch, ApiRequestError, CSRF_COOKIE_NAME } from '@/lib/api';

// ---------- query keys ----------

export const authKeys = {
  me: ['auth', 'me'] as const,
  security: ['auth', 'security'] as const,
};

// ---------- /auth/me ----------

export interface UseMeOptions {
  enabled?: boolean;
}

type AuthSessionCache = authContracts.AuthSessionResponse | null;
const AUTH_QUERY_SCOPE = 'auth';
const SENSITIVE_LOCAL_STORAGE_KEYS = [
  'personal-blog.activeGroupSlug',
  'viquard.editor.draft.v1',
  'personal-blog.library.uploadQueue.v1',
] as const;

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${encodeURIComponent(name)}=`;
  const found = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!found) return null;
  return decodeURIComponent(found.slice(prefix.length));
}

export function hasAuthSessionHint(): boolean {
  return readCookie(CSRF_COOKIE_NAME) !== null;
}

export function clearAuthSessionHint(): void {
  if (typeof document === 'undefined') return;
  document.cookie = [
    `${encodeURIComponent(CSRF_COOKIE_NAME)}=`,
    'Path=/',
    'Max-Age=0',
    'Expires=Thu, 01 Jan 1970 00:00:00 GMT',
  ].join('; ');
}

export function clearAuthSensitiveBrowserState(): void {
  if (typeof window === 'undefined') return;
  for (const key of SENSITIVE_LOCAL_STORAGE_KEYS) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Browser storage can throw in private / locked-down contexts.
    }
  }
}

export async function clearViewerScopedQueryCache(qc: QueryClient): Promise<void> {
  const filters = {
    predicate: (query: { queryKey: readonly unknown[] }): boolean =>
      query.queryKey[0] !== AUTH_QUERY_SCOPE,
  };
  await qc.cancelQueries(filters, { revert: false });
  void qc.resetQueries(filters, { cancelRefetch: true });
  qc.removeQueries(filters);
}

export async function resetAuthSessionCache(
  qc: QueryClient,
  session: AuthSessionCache,
): Promise<void> {
  clearAuthSensitiveBrowserState();
  await clearViewerScopedQueryCache(qc);
  qc.setQueryData(authKeys.me, session);
}

export function useMe(options: UseMeOptions = {}): UseQueryResult<AuthSessionCache, ApiRequestError> {
  return useQuery({
    queryKey: authKeys.me,
    queryFn: () => apiFetch<authContracts.AuthSessionResponse>('/api/auth/me'),
    enabled: options.enabled ?? true,
    // 401 from /me means "not logged in"; transport failures also wait for manual retry.
    retry: false,
  });
}

// ---------- mutations ----------

interface MutationDeps {
  invalidate?: boolean;
}

export function useRegister(): UseMutationResult<
  authContracts.AuthRegisterResponse,
  ApiRequestError,
  authContracts.RegisterRequest
> {
  return useMutation({
    mutationFn: (body) =>
      apiFetch<authContracts.AuthRegisterResponse>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

export function useLogin(): UseMutationResult<
  authContracts.AuthSessionResponse,
  ApiRequestError,
  authContracts.LoginRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<authContracts.AuthSessionResponse>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      await resetAuthSessionCache(qc, data);
    },
  });
}

export function useLogout(): UseMutationResult<
  authContracts.LogoutResponse,
  ApiRequestError,
  void
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<authContracts.LogoutResponse>('/api/auth/logout', { method: 'POST' }),
    onSuccess: async () => {
      await resetAuthSessionCache(qc, null);
    },
  });
}

export function useAuthSecurityOverview(): UseQueryResult<
  authContracts.AuthSecurityOverviewResponse,
  ApiRequestError
> {
  return useQuery({
    queryKey: authKeys.security,
    queryFn: () =>
      apiFetch<authContracts.AuthSecurityOverviewResponse>('/api/auth/me/security'),
  });
}

export function useRevokeAuthSession(): UseMutationResult<
  authContracts.LogoutResponse,
  ApiRequestError,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId) =>
      apiFetch<authContracts.LogoutResponse>(
        `/api/auth/me/sessions/${encodeURIComponent(sessionId)}/revoke`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: authKeys.security });
    },
  });
}

export function useChangeUsername(): UseMutationResult<
  authContracts.AuthSessionResponse,
  ApiRequestError,
  authContracts.ChangeUsernameRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<authContracts.AuthSessionResponse>('/api/auth/me/username', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      qc.setQueryData(authKeys.me, data);
    },
  });
}

export function useStartEmailBind(): UseMutationResult<
  authContracts.EmailBindStartResponse,
  ApiRequestError,
  authContracts.StartEmailBindRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<authContracts.EmailBindStartResponse>('/api/auth/me/email/bind/start', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      qc.setQueryData(authKeys.me, {
        user: data.user,
        requiresVerification: data.requiresVerification,
      } satisfies authContracts.AuthSessionResponse);
    },
  });
}

export function useStartPhoneBind(): UseMutationResult<
  authContracts.PhoneBindStartResponse,
  ApiRequestError,
  authContracts.StartPhoneBindRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<authContracts.PhoneBindStartResponse>('/api/auth/me/phone/bind/start', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      qc.setQueryData(authKeys.me, {
        user: data.user,
        requiresVerification: data.requiresVerification,
      } satisfies authContracts.AuthSessionResponse);
    },
  });
}

export function useConfirmPhoneBind(): UseMutationResult<
  authContracts.AuthSessionResponse,
  ApiRequestError,
  authContracts.ConfirmPhoneBindRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<authContracts.AuthSessionResponse>('/api/auth/me/phone/bind/confirm', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      qc.setQueryData(authKeys.me, data);
    },
  });
}

export function useRequestPasswordReset(
  _opts: MutationDeps = {},
): UseMutationResult<
  authContracts.RequestPasswordResetResponse,
  ApiRequestError,
  authContracts.RequestPasswordResetRequest
> {
  return useMutation({
    mutationFn: (body) =>
      apiFetch<authContracts.RequestPasswordResetResponse>('/api/auth/password/request-reset', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

export function useRequestChildPasswordReset(): UseMutationResult<
  authContracts.RequestPasswordResetResponse,
  ApiRequestError,
  authContracts.RequestChildPasswordResetRequest
> {
  return useMutation({
    mutationFn: (body) =>
      apiFetch<authContracts.RequestPasswordResetResponse>(
        '/api/auth/password/request-child-reset',
        { method: 'POST', body: JSON.stringify(body) },
      ),
  });
}

export function useResetPassword(): UseMutationResult<
  authContracts.LogoutResponse,
  ApiRequestError,
  authContracts.ResetPasswordRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<authContracts.LogoutResponse>('/api/auth/password/reset', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      // Server killed every active session; clear our cache too.
      await resetAuthSessionCache(qc, null);
    },
  });
}

export function useResetPasswordWithPhone(): UseMutationResult<
  authContracts.LogoutResponse,
  ApiRequestError,
  authContracts.ResetPasswordWithPhoneRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<authContracts.LogoutResponse>('/api/auth/password/reset/phone', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await resetAuthSessionCache(qc, null);
    },
  });
}

// ---------- verification ----------

export function verifyEmailRequest(
  body: authContracts.EmailVerifyRequest,
): Promise<authContracts.VerificationResponse> {
  return apiFetch<authContracts.VerificationResponse>('/api/auth/verify/email', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function useVerifyEmail(): UseMutationResult<
  authContracts.VerificationResponse,
  ApiRequestError,
  authContracts.EmailVerifyRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: verifyEmailRequest,
    onSuccess: (data) => {
      if (data.session !== null) {
        void resetAuthSessionCache(qc, data.session);
        return;
      }
      void qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

export function useVerifyPhone(): UseMutationResult<
  authContracts.VerificationResponse,
  ApiRequestError,
  authContracts.PhoneVerifyRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch<authContracts.VerificationResponse>('/api/auth/verify/phone', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      if (data.session !== null) {
        void resetAuthSessionCache(qc, data.session);
        return;
      }
      void qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

export function useResendVerification(): UseMutationResult<
  authContracts.ResendVerificationResponse,
  ApiRequestError,
  authContracts.ResendVerificationRequest
> {
  return useMutation({
    mutationFn: (body) =>
      apiFetch<authContracts.ResendVerificationResponse>('/api/auth/verify/resend', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

// ---------- error helper ----------

/** Map an ApiRequestError → a key under the `auth` namespace's `errors.*`.
 *  Returns the un-namespaced path so callers using `useTranslation('auth')`
 *  resolve correctly. Falls back to UNKNOWN. */
export function authErrorKey(err: unknown): string {
  if (err instanceof ApiRequestError) {
    return `errors.${err.code}`;
  }
  return 'errors.UNKNOWN';
}
